import { Component, Inject, Input, OnChanges, OnInit, Optional, SimpleChanges } from '@angular/core';
import { Grid } from '@app/core/components/grid/grid.model';
import { GridOptions } from '@app/core/components/grid/grid.types';
import { ModalService } from '@app/core/services/modal.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';
import { AcSuppTransesGet, AcSuppTransesGridRow, AcSuppTransService, GetAcSuppTransesParams } from '@app/services/ac-supp-trans.service';
import { BusinesscoItem, LogisticService } from '@app/services/logistic.service';
import { CustomerAccountInfo, TypeaheadService } from '@app/services/typeahead.service';

@Component({
    selector: 'tt-ac-supp-transes',
    templateUrl: './ac-supp-transes.component.html',
    styleUrls: ['./ac-supp-transes.component.css'],
    standalone: false,
})
export class AcSuppTransesComponent implements OnInit, OnChanges {
    @Input()
    public params?: GetAcSuppTransesParams;

    public page?: AcSuppTransesGet;

    public companies: BusinesscoItem[] = [];

    public grid?: GridOptions<AcSuppTransesGridRow>;

    public removing = false;

    public matching = false;

    public autoMatching = false;

    constructor(
        private acSuppTrans: AcSuppTransService,
        private logistic: LogisticService,
        private typeahead: TypeaheadService,
        private modal: ModalService,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    public searchSuppliers = ({ value }: { value: string | null }): Promise<CustomerAccountInfo[]> => {
        return this.typeahead.searchCustomerAccountInfo({ cust_name: value ?? '', custtype: 'S' });
    };

    public readGrid(): void {
        this.grid?.gridfunc?.read({ reloadColschema: false });
    }

    public readGridWithColschema(): void {
        this.grid?.gridfunc?.read({ reloadColschema: true });
    }

    public async removeMatching(): Promise<void> {
        if (!this.page) return;

        try {
            this.removing = true;

            const { errorcode, errormessage } = await this.acSuppTrans.removeAcSuppTransesMatching(this.page);

            if (errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'acsupptranses_remove_matching_show_title', translateTitle: true, message: errormessage });
            } else {
                this.readGrid();
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.removing = false;
        }
    }

    public async match(): Promise<void> {
        if (!this.page) return;

        try {
            this.matching = true;

            const records = (this.grid?.gridfunc?.getIsSelectedRows() ?? []).map(({ acsupptrans_keyno }) => ({ acsupptrans_keyno }));
            const { errorcode, errormessage } = await this.acSuppTrans.matchSelectedAcSuppTranses({ ...this.page, records });

            if (errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'acsupptranses_match_selected_show_title', translateTitle: true, message: errormessage });
            } else {
                this.readGrid();
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.matching = false;
        }
    }

    public async autoMatch(): Promise<void> {
        if (!this.page) return;

        try {
            this.autoMatching = true;

            const { errorcode, errormessage } = await this.acSuppTrans.autoMatchAcSuppTranses(this.page);

            if (errorcode !== '0') {
                this.modal.show({ type: 'warning', title: 'acsupptranses_match_auto_show_title', translateTitle: true, message: errormessage });
            } else {
                this.readGrid();
            }
        } catch (error) {
            console.error(error);
        } finally {
            this.autoMatching = false;
        }
    }

    private loadGrid(): void {
        if (!this.page) return;

        if (!this.grid) {
            this.grid = new Grid<AcSuppTransesGridRow>()
                .setRememberId('w_acsupptranses_grid')
                .setLoadDataFunction({ method: 2792, parameters: () => this.getGridParams() })
                .setSpecialFunc({ newTab: true })
                .setOptionFunc((event: AcSuppTransesGridEvent) => this.optionFunc(event.data));
        } else {
            this.readGridWithColschema();
        }
    }

    private getGridParams(): Omit<AcSuppTransesGet, 'suppliers'> & { acc_supp_nos: string } {
        const { suppliers, ...page } = this.page!;

        return { ...page, acc_supp_nos: suppliers.map(({ acc_supp_no }) => acc_supp_no).join(',') };
    }

    private optionFunc(event: AcSuppTransesGridOption): void {
        if (event.func === 'CheckboxBoxClick' && event.dataItem) {
            event.dataItem.is_selected_amount_locval_balance = event.dataItem.is_selected === true ? event.dataItem.amount_locval_balance : 0;
        } else if (event.func === 'CheckboxHeaderClick') {
            event.data?.forEach((row) => {
                row.is_selected_amount_locval_balance = row.is_selected === true ? row.amount_locval_balance : 0;
            });
        } else {
            return;
        }

        this.grid?.gridfunc?.refresh();
        this.grid?.gridfunc?.refreshAggregates();
    }

    private async init(): Promise<void> {
        if (!this.params) return;

        try {
            const [page, companies] = await Promise.all([this.acSuppTrans.getAcSuppTranses(this.params), this.logistic.getCompanies()]);

            this.page = page;
            this.companies = companies;
            this.navigator?.setHeading?.(page.page_heading);
            this.loadGrid();
        } catch (error) {
            console.error(error);
        }
    }

    ngOnInit(): void {
        if (this.navigator) {
            this.navigator.setSize?.({ width: '95vw', height: '98vh' });
            this.params = this.navigator.params as unknown as GetAcSuppTransesParams;
        }

        this.init();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params'] && !changes['params'].firstChange) {
            this.init();
        }
    }
}

interface AcSuppTransesGridEvent {
    data: AcSuppTransesGridOption;
}

interface AcSuppTransesGridOption {
    func?: 'CheckboxBoxClick' | 'CheckboxHeaderClick' | string;
    dataItem?: AcSuppTransesGridRow;
    data?: AcSuppTransesGridRow[];
}
